import { useState } from "react";
import { useHistory } from "react-router";
import { SweetAlertResult } from "sweetalert2";
import { common_utils } from "../../../../../../common/common_utils";
import { Constants } from "../../../../../../common/constants";
import {
	DeleteRequest,
	DiscordAuthToken,
	EventDeleteType,
	EventDisplayData,
	IDeleteEventResult,
} from "../../../../../../common/sesh_types";
import { RouterInferenceHelpers } from "../../../../../../src/api/trpc/rootRouter";
import { DashboardUtils } from "../../../dashboard_utils";
import { trpc } from "../../../utils/trpc";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";
import { DropdownItem } from "../base_components/with_dropdown_improved";
import { BoxItem } from "../box_item";
import { useHistoryPusher } from "../listings_form/event/calendar/hooks/use_history_pusher";
import { usePublicCalendarContext } from "../listings_form/event/calendar/hooks/use_public_calendar_context";
import { getRepeatData } from "../listings_form/event/event_listing_form/event_row";
import { EditEventTimeContent } from "./edit_event_time_content";
import { useIsAdmin } from "./use_is_admin";
import { useServerId } from "./use_server_id";
import { useUserContext } from "./use_user_context";

function LinkOrAnchorDropdownItemProps(
	opts: { is_public_calendar: boolean },
	props: Omit<DropdownItem, "type" | "props"> & { url: string }
): DropdownItem {
	if (opts.is_public_calendar) {
		return {
			...props,
			type: "a",
			props: {
				href: props.url,
			},
		};
	}

	return { ...props, type: "link", props: { to: props.url } };
}

export function useGenerateEventActions(
	event: EventDisplayData,
	is_removing: boolean,
	setIsRemoving: (is_removing: boolean) => void,
	dropdown_parent_id?: string,
	disable_rrule_check = false
):
	| (Record<"edit" | "clone" | "open" | "delete", DropdownItem> & {
			isLoading: false;
	  })
	| { isLoading: true } {
	const server_id = useServerId();

	const { is_public: is_public_calendar } = usePublicCalendarContext();

	const user = useUserContext();
	const is_admin_query = useIsAdmin({
		disabled: is_public_calendar,
	});

	const history = useHistory();

	const context = trpc.useContext();
	const is_event_creator = user?.id === event.author_id;
	const has_edit_event_perms =
		is_public_calendar || is_admin_query.data || is_event_creator;
	const has_event_started = new Date(event.start_time_str) < new Date();
	const cannot_edit_event_tooltip_text =
		"Must be event creator or have Manage Sesh permissions to modify events";

	const exdate_mutation = trpc.event.deleteRepeatOccurrence.useMutation();

	if (!is_public_calendar && (is_removing || !is_admin_query.isSuccess)) {
		return { isLoading: true };
	}

	return {
		isLoading: false,
		edit:
			!disable_rrule_check && event.rrule
				? {
						type: "dropdown",
						icon: <i className="fas fa-pencil-alt"></i>,
						label: "Edit event",
						props: {
							parent_id: dropdown_parent_id,
							dropdown_element: (
								<RepeatEditDropdown event={event} />
							),
						},
						disabled_state: {
							is_disabled:
								has_event_started || !has_edit_event_perms,
							tooltip: !has_edit_event_perms
								? cannot_edit_event_tooltip_text
								: "Can not edit completed or ongoing events",
						},
				  }
				: LinkOrAnchorDropdownItemProps(
						{ is_public_calendar },
						{
							icon: <i className="fas fa-pencil-alt"></i>,
							label: "Edit event",
							disabled_state: {
								is_disabled:
									has_event_started || !has_edit_event_perms,
								tooltip: !has_edit_event_perms
									? cannot_edit_event_tooltip_text
									: "Can not edit completed or ongoing events",
							},
							url: common_utils.getEventEditURL(
								server_id,
								event.message_id
							),
						}
				  ),
		clone: LinkOrAnchorDropdownItemProps(
			{ is_public_calendar },
			{
				icon: <i className="fas fa-clone"></i>,
				label: "Use as template",
				url: common_utils.getEventCreateURL(server_id, undefined, {
					id: event.message_id,
					type: "event",
				}),
			}
		),
		open: {
			type: "a",
			icon: <i className="fas fa-external-link-alt" />,
			label: "Open in Discord",
			props: {
				href: `${Constants.DISCORD_LINK_PREFIX}${server_id}/${event.channel_id}/${event.message_id}`.replace(
					"https",
					"discord"
				),
			},
			disabled_state: {
				is_disabled: event.is_repeat_occurrence,
				tooltip: "Event has not been posted yet",
			},
		},
		delete: !is_public_calendar
			? {
					type: "button",
					icon: <i className="far fa-trash-alt" />,
					label: "Delete event",
					is_warning: true,
					props: {
						onClick: () => {
							if (!has_edit_event_perms) {
								return;
							}

							onDeleteClicked(
								context,
								event,
								(delete_type) => {
									if (event.is_repeat_occurrence) {
										return exdate_mutation.mutateAsync({
											date: event.start_time_str,
											repeat_message_id:
												event.original_repeat_message_id,
											type: delete_type,
										});
									}

									return deleteEvent(
										user,
										server_id,
										event.message_id,
										delete_type
									);
								},
								setIsRemoving
							);
						},
					},
					disabled_state: {
						is_disabled: !has_edit_event_perms,
						tooltip: cannot_edit_event_tooltip_text,
					},
			  }
			: undefined,
	};
}

function RepeatEditDropdown(props: { event: EventDisplayData }) {
	const server_id = useServerId();

	const pushHistory = useHistoryPusher();

	const [is_modal_open, setIsModalOpen] = useState(false);

	return (
		<>
			<BoxItem
				bgColor="semidark"
				className="border border-sesh-primary shadow-lg"
			>
				<div className="flex w-64 flex-col gap-2 text-sm">
					<SeshPrimaryButton
						custom_size="small"
						onClick={() => setIsModalOpen(true)}
					>
						Edit This Instance
					</SeshPrimaryButton>
					<SeshPrimaryButton
						custom_size="small"
						onClick={() =>
							pushHistory(
								common_utils.getEventEditURL(
									server_id,
									props.event.message_id
								)
							)
						}
					>
						Edit All Instances
					</SeshPrimaryButton>
				</div>
			</BoxItem>
			{is_modal_open && (
				<DashboardUtils.SeshModal
					is_active
					allow_overflow
					title={props.event.name}
					content={
						<EditTimeModalContent
							event={
								props.event as RouterInferenceHelpers["event"]["list"]["output"]["repeat_items"][number]
							}
							close={() => setIsModalOpen(false)}
						/>
					}
					onClose={() => setIsModalOpen(false)}
				/>
			)}
		</>
	);
}

function EditTimeModalContent(props: {
	event: RouterInferenceHelpers["event"]["list"]["output"]["repeat_items"][number];
	close: () => void;
}) {
	const context = trpc.useContext();

	const mutation = trpc.event.addRepeatRDate.useMutation({
		onSuccess: () => {
			context.event.list.invalidate();
			props.close();
		},
	});

	return (
		<EditEventTimeContent
			repeat_start_time={
				new Date(props.event.repeat_instance_original_start_time_str)
			}
			original_instance_start_time={
				new Date(props.event.initial_event_start_time_str)
			}
			start_time={new Date(props.event.start_time_str)}
			end_time={new Date(props.event.end_time_str)}
			rrule={props.event.rrule}
			repeat_tz={props.event.repeat_tz}
			exdates={props.event.exdates}
			rdates={props.event.rdates}
			onSubmit={(date: Date) =>
				mutation.mutate({
					orig_date: props.event.start_time_str,
					new_date: date.toISOString(),
					repeat_message_id: props.event.original_repeat_message_id,
				})
			}
			is_loading={mutation.isLoading}
		/>
	);
}

async function deleteEvent(
	auth_token: DiscordAuthToken,
	guild_id: string,
	message_id: string,
	delete_type: EventDeleteType = "Single"
) {
	const request_body: DeleteRequest = {
		access_token: auth_token.access_token,
		token_type: auth_token.token_type,
		guild_id,
		message_id,
		delete_type,
	};

	return fetch("/api/delete_event", {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(request_body),
	}).then((response) =>
		response.json().then((value: IDeleteEventResult) => {
			if (value.err) {
				DashboardUtils.ShowDefaultErrorAlert(value.err);
			}
			return value;
		})
	);
}

export async function onDeleteClicked(
	context: ReturnType<typeof trpc.useContext>,
	event: RouterInferenceHelpers["event"]["getListItem"]["output"],
	deleteFunc: (delete_type: EventDeleteType) => Promise<IDeleteEventResult>,
	setEventPendingDelete?: (is_deleting: boolean) => void
) {
	const end_time = new Date(event.end_time_str);
	const repeat_data = getRepeatData(event);

	let confirmation_result: SweetAlertResult<any>;
	if (repeat_data && end_time > new Date()) {
		// Only display this if its the most current repeat occurrence
		confirmation_result = await DashboardUtils.ShowDeleteRepeatConfirmAlert(
			<span>
				Would you like to delete only this event or this event and all
				future occurrences of{" "}
				<span className="has-text-weight-bold">{event.name}</span>?
				{!event.is_repeat_occurrence && (
					<>
						{" "}
						If you delete only this event, the next event will be
						posted immediately.
					</>
				)}
				<br />
				<br />
				<span className="has-text-weight-bold">Repeats:</span>{" "}
				{repeat_data.repeat_text}
			</span>
		);
	} else {
		confirmation_result = await DashboardUtils.ShowDeleteConfirmAlert(
			<span>
				Are you sure you want to permanently delete event{" "}
				<span className="has-text-weight-bold">{event.name}</span>?{" "}
				{repeat_data && (
					<>Delete the latest event to stop future repeats.</>
				)}
			</span>
		);
	}

	// Confirm = Single Delete - Deny = Full Delete
	if (!confirmation_result.isConfirmed && !confirmation_result.isDenied) {
		return;
	}

	const delete_type: EventDeleteType = confirmation_result.isConfirmed
		? "Single"
		: "Single_And_Future";

	setEventPendingDelete?.(true);

	deleteFunc(delete_type).then((val) => {
		setEventPendingDelete?.(false);
		if (val.db_success) {
			context.event.list.invalidate();
		}

		if (val.repeat_delete_result?.err) {
			DashboardUtils.ShowDefaultErrorAlert(
				common_utils.markdownToHtml(val.repeat_delete_result.err)
			);
		}
	});
}
